import dayjs from 'dayjs'
import * as React from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '#/shared/lib/utils'
import { Button } from '#/shared/ui/button'
import { DatePicker } from '#/shared/ui/date-picker'
import { EditorEmpty } from '#/shared/ui/editor-empty'
import {
  CheckRoundedIcon,
  EmailIcon,
  PaymentIcon,
  PhoneChatIcon,
  UserPersonIcon,
} from '#/shared/ui/icons'
import { ClearButton } from '#/shared/ui/input'
import { MultiSelect } from '#/shared/ui/multi-select'
import type { MultiSelectOption } from '#/shared/ui/multi-select'
import { VoiceInputButton } from '#/shared/ui/voice-recording-island'

type TaskType = 'phone' | 'email' | 'payment' | 'general'
type TaskPriority = 'low' | 'medium' | 'high'
type QuickDatePreset = 'today' | 'tomorrow' | 'nextWeek'

interface TaskValue {
  task: string
  type: TaskType
  date?: Date
  time: string
  priority: TaskPriority
  users: string[]
}

interface TaskEditorProps {
  open?: boolean
  description?: string
  value?: TaskValue
  defaultValue?: TaskValue
  onChange?: (next: TaskValue) => void
  onCreate?: () => void
  onCancel?: () => void
  onSave?: (value: TaskValue) => void
  className?: string
  isSubmitting?: boolean
  userOptions?: MultiSelectOption[]
  usersLoading?: boolean
  userSearch?: string
  onUserSearchChange?: (q: string) => void
}

const DEFAULT_VALUE: TaskValue = {
  task: '',
  type: 'phone',
  date: undefined,
  time: '',
  priority: 'medium',
  users: [],
}

const TYPE_ICON: Record<
  TaskType,
  React.ComponentType<{ className?: string }>
> = {
  phone: PhoneChatIcon,
  email: EmailIcon,
  payment: PaymentIcon,
  general: CheckRoundedIcon,
}

function computeQuickDate(preset: QuickDatePreset): Date {
  const today = dayjs().startOf('day')
  if (preset === 'today') return today.toDate()
  if (preset === 'tomorrow') return today.add(1, 'day').toDate()
  return today.add(7, 'day').toDate()
}

function FieldRow({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <div className="flex w-full items-center gap-2.5">
      <p className="w-12.5 shrink-0 text-right text-sm text-gray-500">
        {label}
      </p>
      <div className="flex min-w-0 flex-1 items-center">{children}</div>
    </div>
  )
}

function TypePill({
  type,
  label,
  active,
  onClick,
}: {
  type: TaskType
  label: string
  active: boolean
  onClick: () => void
}) {
  const Icon = TYPE_ICON[type]
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'flex h-7 shrink-0 cursor-pointer items-center gap-1.25 rounded-full border px-2.5 text-sm transition-colors',
        active
          ? 'border-primary bg-blue-100 text-primary'
          : 'border-gray-200 text-gray-500 hover:bg-gray-50',
      )}
    >
      <Icon className="size-4.5 shrink-0" />
      <span className="whitespace-nowrap leading-none">{label}</span>
    </button>
  )
}

function TypeSelector({
  value,
  onChange,
}: {
  value: TaskType
  onChange: (v: TaskType) => void
}) {
  const { t } = useTranslation()
  const types: TaskType[] = ['phone', 'email', 'payment', 'general']
  return (
    <div className="flex items-center gap-2.5">
      {types.map((type) => (
        <TypePill
          key={type}
          type={type}
          label={t(`editor.task.types.${type}`)}
          active={value === type}
          onClick={() => onChange(type)}
        />
      ))}
    </div>
  )
}

function UsersEmptyState() {
  const { t } = useTranslation()
  return (
    <div className="flex animate-in fade-in slide-in-from-top-1 flex-col items-center gap-2 px-5 py-6 text-center duration-300 ease-out">
      <div className="flex size-10 items-center justify-center rounded-full bg-gray-100 text-gray-500">
        <UserPersonIcon className="size-5" />
      </div>
      <p className="text-sm font-medium text-gray-900">
        {t('editor.task.users.empty')}
      </p>
      <p className="text-xs text-gray-500">
        {t('editor.task.users.emptyHint')}
      </p>
    </div>
  )
}

function QuickDateChips({
  onSelect,
}: {
  onSelect: (preset: QuickDatePreset) => void
}) {
  const { t } = useTranslation()
  const presets: QuickDatePreset[] = ['today', 'tomorrow', 'nextWeek']
  return (
    <div className="flex items-center gap-2.5">
      {presets.map((p) => (
        <button
          key={p}
          type="button"
          onClick={() => onSelect(p)}
          className="flex h-6 shrink-0 transform-gpu cursor-pointer items-center whitespace-nowrap rounded-md border border-gray-200 px-2.5 text-sm text-gray-500 outline-none transition-[transform,background-color,color,border-color] duration-150 ease-out hover:bg-gray-50 focus:focus-ring active:scale-[0.98]"
        >
          {t(`editor.task.quickDates.${p}`)}
        </button>
      ))}
    </div>
  )
}

function PrioritySegmented({
  value,
  onChange,
}: {
  value: TaskPriority
  onChange: (v: TaskPriority) => void
}) {
  const { t } = useTranslation()
  const priorities: TaskPriority[] = ['low', 'medium', 'high']
  const PRIORITY_STYLES: Record<TaskPriority, { bg: string; text: string }> = {
    low: { bg: 'bg-green-50', text: 'text-green-500' },
    medium: { bg: 'bg-orange-100', text: 'text-orange-500' },
    high: { bg: 'bg-red-100', text: 'text-red-500' },
  }
  const containerRef = React.useRef<HTMLDivElement | null>(null)
  const btnRefs = React.useRef<Record<string, HTMLButtonElement | null>>({})
  const [indicator, setIndicator] = React.useState<{
    left: number
    width: number
  } | null>(null)

  const update = React.useCallback(() => {
    const el = btnRefs.current[value]
    if (!el) return
    setIndicator({ left: el.offsetLeft, width: el.offsetWidth })
  }, [value])

  React.useLayoutEffect(() => {
    update()
  }, [update])

  React.useEffect(() => {
    const handler = () => update()
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [update])

  return (
    <div
      ref={containerRef}
      className="relative flex h-7 items-center overflow-hidden rounded-md border border-gray-200"
    >
      {indicator && (
        <span
          aria-hidden="true"
          className={cn(
            'pointer-events-none absolute inset-y-0 z-0 transition-[left,width,background-color] duration-300 ease-out',
            PRIORITY_STYLES[value].bg,
          )}
          style={{ left: indicator.left, width: indicator.width }}
        />
      )}
      {priorities.map((p, i) => {
        const active = value === p
        return (
          <React.Fragment key={p}>
            {i > 0 && (
              <span
                aria-hidden="true"
                className="relative z-10 h-full w-px bg-gray-200"
              />
            )}
            <button
              ref={(el) => {
                btnRefs.current[p] = el
              }}
              type="button"
              aria-pressed={active}
              onClick={() => onChange(p)}
              className={cn(
                'relative z-10 flex h-full cursor-pointer items-center px-2.5 text-sm transition-colors',
                active ? PRIORITY_STYLES[p].text : 'text-gray-500',
              )}
            >
              {t(`editor.task.priorities.${p}`)}
            </button>
          </React.Fragment>
        )
      })}
    </div>
  )
}

function TaskForm({
  value,
  onChange,
  onCancel,
  onSave,
  isSubmitting,
  userOptions,
  usersLoading,
  userSearch,
  onUserSearchChange,
}: {
  value: TaskValue
  onChange: (v: TaskValue) => void
  onCancel?: () => void
  onSave?: (v: TaskValue) => void
  isSubmitting?: boolean
  userOptions: MultiSelectOption[]
  usersLoading: boolean
  userSearch?: string
  onUserSearchChange?: (q: string) => void
}) {
  const { t } = useTranslation()
  const patch = (partial: Partial<TaskValue>) =>
    onChange({ ...value, ...partial })
  return (
    <div className="flex w-full flex-col gap-2.5 pt-2.5">
      <div className="flex w-full flex-col gap-5 px-3.75">
        <FieldRow label={t('editor.task.fields.task')}>
          <div className="flex h-8 w-full items-center gap-1.25 border-b border-gray-200 px-2.5">
            <input
              type="text"
              value={value.task}
              onChange={(e) => patch({ task: e.target.value })}
              placeholder={t('editor.task.placeholder')}
              className="min-w-0 flex-1 bg-transparent text-sm text-gray-900 placeholder:text-gray-300 focus:outline-none"
            />
            <ClearButton
              onClick={() => patch({ task: '' })}
              hidden={!value.task}
            />
            <VoiceInputButton
              value={value.task}
              onChange={(task) => patch({ task })}
            />
          </div>
        </FieldRow>

        <div className="flex w-full flex-col gap-3.75">
          <FieldRow label={t('editor.task.fields.type')}>
            <TypeSelector
              value={value.type}
              onChange={(type) => patch({ type })}
            />
          </FieldRow>

          <FieldRow label={t('editor.task.fields.date')}>
            <div className="flex items-center gap-7.5">
              <div className="flex items-center gap-2.5">
                <DatePicker
                  value={value.date}
                  onValueChange={(date) => patch({ date })}
                  placeholder="Jan 21, 2026"
                  dateFormat="MMM D, YYYY"
                  className="w-30"
                />
                <input
                  type="time"
                  step="60"
                  value={value.time}
                  onChange={(e) => patch({ time: e.target.value })}
                  className="h-8 w-22.5 appearance-none rounded-md border border-gray-200 bg-white px-2.5 text-sm text-gray-500 outline-none focus-visible:focus-ring [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none"
                />
              </div>
              <QuickDateChips
                onSelect={(preset) =>
                  patch({ date: computeQuickDate(preset), time: '00:00' })
                }
              />
            </div>
          </FieldRow>

          <FieldRow label={t('editor.task.fields.priority')}>
            <PrioritySegmented
              value={value.priority}
              onChange={(priority) => patch({ priority })}
            />
          </FieldRow>

          <FieldRow label={t('editor.task.fields.user')}>
            <div className="flex min-w-0 flex-1">
              <MultiSelect
                options={userOptions}
                value={value.users}
                onValueChange={(users) => patch({ users })}
                loading={usersLoading}
                placeholder={t('editor.task.addUser')}
                searchPlaceholder={t('editor.task.searchUser')}
                disabled={isSubmitting}
                emptyMessage={<UsersEmptyState />}
                searchValue={userSearch}
                onSearchChange={onUserSearchChange}
              />
            </div>
          </FieldRow>
        </div>
      </div>

      <div className="flex h-14 items-center justify-end gap-2.5 border-t border-gray-200 px-2.5">
        {onCancel && (
          <Button
            variant="outline"
            size="xs"
            pressEffect
            disabled={isSubmitting}
            onClick={onCancel}
          >
            {t('editor.task.cancel')}
          </Button>
        )}
        {onSave && (
          <Button
            size="xs"
            pressEffect
            disabled={isSubmitting}
            onClick={() => onSave(value)}
          >
            {isSubmitting && (
              <span
                aria-hidden
                className="size-3.5 animate-spin rounded-full border-2 border-current border-t-transparent"
              />
            )}
            {isSubmitting ? t('table.saving') : t('editor.task.save')}
          </Button>
        )}
      </div>
    </div>
  )
}

function TaskEditor({
  open,
  description,
  value,
  defaultValue,
  onChange,
  onCreate,
  onCancel,
  onSave,
  className,
  isSubmitting,
  userOptions = [],
  usersLoading = false,
  userSearch,
  onUserSearchChange,
}: TaskEditorProps) {
  const { t } = useTranslation()
  const [internal, setInternal] = React.useState<TaskValue>(
    defaultValue ?? DEFAULT_VALUE,
  )
  const current = value ?? internal
  const handleChange = (next: TaskValue) => {
    if (value === undefined) setInternal(next)
    onChange?.(next)
  }

  const resolvedDescription = description ?? t('editor.task.description')
  const showForm = open ?? false

  return (
    <div className={cn('flex w-full flex-col', className)}>
      <div
        key={showForm ? 'form' : 'empty'}
        className="w-full animate-in fade-in slide-in-from-top-1 duration-200 ease-out"
      >
        {showForm ? (
          <TaskForm
            value={current}
            onChange={handleChange}
            onCancel={onCancel}
            onSave={onSave}
            isSubmitting={isSubmitting}
            userOptions={userOptions}
            usersLoading={usersLoading}
            userSearch={userSearch}
            onUserSearchChange={onUserSearchChange}
          />
        ) : (
          <EditorEmpty
            description={resolvedDescription}
            actionLabel={t('editor.task.create')}
            onAction={onCreate}
          />
        )}
      </div>
    </div>
  )
}

function formatTaskDate(date: Date | undefined): string {
  return date ? dayjs(date).format('MMM D, YYYY') : ''
}

export { TaskEditor, formatTaskDate }
export type {
  TaskEditorProps,
  TaskValue,
  TaskType,
  TaskPriority,
  QuickDatePreset,
}
